import { useEffect, useState } from 'react';
import { devError, devLog, isBrowser, isMobile } from './browser';

export function useLayout(disabled = false) {
  const [mobile, setMobile] = useState(false);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  // eslint-disable-next-line no-unused-vars
  const handleResize = (_event) => {
    setMobile(isMobile());
    setWidth(window.innerWidth);
    setHeight(window.innerHeight);
  };

  useEffect(() => {
    if (!isBrowser() || disabled) return () => {};

    devLog('[useLayout] add event listeners');
    window.addEventListener('resize', handleResize);
    window.addEventListener('load', handleResize);

    setTimeout(() => {
      handleResize();
    }, 200);

    return () => {
      devLog('[useLayout] removing event listeners...');
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('load', handleResize);
    };
  }, [disabled]);

  return {
    mobile,
    width,
    height,
  };
}

export function useLockPageScroll(locked = false) {
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    if (!isBrowser())
      return () => {
        setIsLocked(false);
      };

    try {
      const className = 'stop-scroll';

      if (locked) {
        document.body.classList.add(className);
        setIsLocked(true);
      }

      return () => {
        document.body.classList.remove(className);
        setIsLocked(false);
      };
    } catch (error) {
      devError('Error locking page scroll:', error);
    }
  }, [locked]);

  return isLocked;
}

export function useScroll(delay = 100) {
  /**
   * @param {ScrollToOptions} options
   * @returns
   */
  const scrollTo = (options, skipDelay = false) => {
    if (!isBrowser()) return;

    setTimeout(
      () => {
        window.scrollTo(options);
      },
      skipDelay ? 1 : delay,
    );
  };

  const scrollToTop = (skipDelay = false) => {
    if (!isBrowser()) return;

    setTimeout(
      () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },
      skipDelay ? 1 : delay,
    );
  };

  const scrollToSelector = (selector = '', skipDelay = false) => {
    if (!isBrowser() || !selector) return;

    setTimeout(
      () => {
        let element = document.querySelector(selector);
        if (!element) return;
        element.scrollIntoView({ behavior: 'smooth' });
      },
      skipDelay ? 1 : delay,
    );
  };

  const dynamicScrollTo = (options) => {
    setTimeout(() => {
      if (!options) return;

      if (options === true || options === 'top') {
        scrollToTop(true);
        return;
      }

      if (typeof options === 'string') {
        scrollToSelector(options, true);
        return;
      }

      scrollTo(options, true);
    }, delay);
  };

  return { scrollToTop, scrollTo, scrollToSelector, dynamicScrollTo };
}
